// ============================================================
// ProjectDetailView — /square/command-center/projects/:projectId
//
// PHASE_2M P1: the URL-routed project summary. A projectId resolves to:
//   - a DIVISION id (strategy/operations/…): division doorway summary
//   - a LIVE portal id (sq_project_portals uuid): the real record summary
// Static /projects/arbuckle keeps its own dedicated route (ranks above this).
//
// Honest states: loading / auth / error / not-found are real; the portal
// summary is the live record (readiness, counts, context). Back to Projects
// + Q home on every state — no dead ends.
// ============================================================

import { COMMAND_CENTER_ROOT } from '../commandCenterRoutes'
import CommandCenterShell, { CC_INK, CC_MONO } from '../shell/CommandCenterShell'
import { OpenIcon, PortalIcon, ProjectIcon } from '../shell/icons'
import { PortalFrame, PROJECTS_ROOT } from './ProjectPortfolio'
import {
  READINESS_META, portalDetailPath, portalContextPath, portalRepoPath, recipientPreviewPath, summarizePortal,
  type PortalDetail as PortalDetailData, type PortalContextRecord,
} from '../olivium/portal-factory/portalFactory'
import { getDivisionWorkspace } from '../workspaces/divisionWorkspaces'
import type { CSSProperties } from 'react'

export interface ProjectDetailViewProps {
  projectId: string
  /** Live portal detail (when projectId is a portal id). */
  detail?: PortalDetailData | null
  context?: PortalContextRecord | null
  loading?: boolean
  error?: string | null
  authRequired?: boolean
  notFound?: boolean
  onOpen?: (path: string) => void
  onHome?: () => void
  onBackCommandCenter?: () => void
  onBackProjects?: () => void
}

const enterBtn = (accent: string): CSSProperties => ({
  padding: '9px 18px', borderRadius: 9, cursor: 'pointer', background: `${accent}14`,
  border: `1px solid ${accent}38`, color: accent, fontSize: 11, fontFamily: CC_MONO, fontWeight: 800, letterSpacing: '0.06em',
})

export default function ProjectDetailView({
  projectId, detail, context, loading, error, authRequired, notFound,
  onOpen, onHome, onBackCommandCenter, onBackProjects,
}: ProjectDetailViewProps) {
  const division = getDivisionWorkspace(projectId)

  const shellProps = {
    onHome, onBackCommandCenter,
    parentBack: { label: 'Back to Projects', onClick: onBackProjects },
    breadcrumb: ['COMMAND CENTER', 'PROJECTS', division ? division.label.toUpperCase() : 'PROJECT'],
    honestyBadge: {
      label: 'PROJECT · LIVE RECORD',
      title: 'A portal projectId renders the real saved record from the live backend; a division id renders its code-registered doorway. Nothing fabricated.',
    },
  }

  if (division) {
    const to = `${COMMAND_CENTER_ROOT}/workspaces/${division.id}`
    return (
      <CommandCenterShell {...shellProps} title={division.label} titleAccent={division.accent}>
        <style>{`@keyframes sqv2RingTrace { 0% { background-position: 0% 0; } 100% { background-position: 300% 0; } } @keyframes sqv2PortalOpen { from { opacity: 0; } to { opacity: 1; } }`}</style>
        <PortalFrame accent={division.accent}>
          <p style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: CC_INK.primary }}>
            <ProjectIcon size={15} color={division.accent} /> Division doorway
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 12.5, color: CC_INK.secondary, lineHeight: 1.6 }}>{division.tagline}</p>
          <button type="button" style={enterBtn(division.accent)} onClick={() => onOpen?.(to)}>OPEN WORKSPACE</button>
        </PortalFrame>
      </CommandCenterShell>
    )
  }

  if (loading) {
    return (
      <CommandCenterShell {...shellProps} title="Opening project…" titleAccent="#7c3aed">
        <p style={{ fontSize: 12, color: CC_INK.faint, fontStyle: 'italic' }}>Loading the live project record…</p>
      </CommandCenterShell>
    )
  }
  if (authRequired) {
    return (
      <CommandCenterShell {...shellProps} title="Sign in required" titleAccent="#b8862a">
        <p style={{ fontSize: 12.5, color: CC_INK.secondary }}>Sign in to the live cockpit to open this project.</p>
      </CommandCenterShell>
    )
  }
  if (error) {
    return (
      <CommandCenterShell {...shellProps} title="Project unavailable" titleAccent="#dc2626">
        <p style={{ fontSize: 12.5, color: '#f87171' }}>{error}</p>
      </CommandCenterShell>
    )
  }
  if (notFound || !detail) {
    return (
      <CommandCenterShell {...shellProps} title="Project not found" titleAccent="#dc2626">
        <p style={{ fontSize: 12.5, color: CC_INK.secondary }}>
          No division or portal matches this id. Use Back to Projects to browse the live portfolio.
        </p>
      </CommandCenterShell>
    )
  }

  const portal = detail.portal
  const meta = READINESS_META[portal.readiness_status] || { label: portal.readiness_status, accent: '#7c3aed' }
  const s = summarizePortal(detail)
  return (
    <CommandCenterShell
      {...shellProps}
      title={portal.project_name}
      titleAccent="#7c3aed"
      statusBadge={<span style={{ fontSize: 9, fontWeight: 800, fontFamily: CC_MONO, letterSpacing: '0.1em', color: meta.accent, padding: '3px 9px', borderRadius: 6, background: `${meta.accent}12`, border: `1px solid ${meta.accent}30` }}>{meta.label.toUpperCase()}</span>}
    >
      <style>{`@keyframes sqv2RingTrace { 0% { background-position: 0% 0; } 100% { background-position: 300% 0; } } @keyframes sqv2PortalOpen { from { opacity: 0; } to { opacity: 1; } }`}</style>
      <PortalFrame accent="#7c3aed">
        {portal.summary && <p style={{ margin: '0 0 12px', fontSize: 12.5, color: CC_INK.secondary, lineHeight: 1.6 }}>{portal.summary}</p>}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14, fontFamily: CC_MONO }}>
          <span style={{ fontSize: 12, color: '#a78bfa' }}>{s.modules} modules</span>
          <span style={{ fontSize: 12, color: '#0891b2' }}>{s.openActions} open actions</span>
          <span style={{ fontSize: 12, color: s.openBlockers > 0 ? '#f87171' : CC_INK.muted }}>{s.openBlockers} open blockers</span>
          <span style={{ fontSize: 12, color: '#059669' }}>{s.receipts} receipts</span>
        </div>
        <div style={{ fontSize: 10.5, fontFamily: CC_MONO, color: CC_INK.faint, marginBottom: 14, display: 'grid', gap: 3 }}>
          <span>portal url · {portalDetailPath(portal.id)}</span>
          {context?.repo_url && <span>repo · {context.repo_url}{context.active_branch ? ` @ ${context.active_branch}` : ''}</span>}
          {context?.deployment_url && <span>deploy · {context.deployment_url}</span>}
          {context?.next_action && <span style={{ color: '#b8862a' }}>next · {context.next_action}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={enterBtn('#7c3aed')} onClick={() => onOpen?.(portalDetailPath(portal.id))}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><PortalIcon size={13} color="#a78bfa" still /> ENTER WORKSPACE</span>
          </button>
          <button type="button" style={enterBtn('#0891b2')} onClick={() => onOpen?.(portalContextPath(portal.id))}>CONTEXT</button>
          <button type="button" style={enterBtn('#059669')} onClick={() => onOpen?.(portalRepoPath(portal.id))}>REPO</button>
          {/* End User Portal — the recipient lifecycle (issue / status / suspend /
              revoke / copy link) lives on the portal workspace; preview renders
              the exact recipient manifest. */}
          <button type="button" style={enterBtn('#b8862a')} onClick={() => onOpen?.(portalDetailPath(portal.id))}>RECIPIENT ACCESS</button>
          <button type="button" style={enterBtn('#e0b054')} onClick={() => onOpen?.(recipientPreviewPath(portal.id))}>PREVIEW AS RECIPIENT</button>
          <button type="button" style={enterBtn(CC_INK.muted)} onClick={() => onOpen?.(PROJECTS_ROOT)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><OpenIcon size={12} /> ALL PROJECTS</span>
          </button>
        </div>
      </PortalFrame>
    </CommandCenterShell>
  )
}
